import React, { ComponentType, useMemo } from 'react';
import { styled, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash';
import useDataSheetDialogActions from 'common/hooks/useDataSheetDialogActions';
import { useLayoutContext } from 'context/LayoutContext';
import { WithTooltipProps } from './withTooltip.props';

const PREFIX = 'withTooltip';

const classes = {
  tooltip: `${PREFIX}-tooltip`,
};

const StyledWrappedComponent = styled('div')(() => ({
  [`& .${classes.tooltip}`]: {
    textAlign: 'left',
    maxWidth: '200px',
  },
}));

// Styles for the wrapped component
const styles = {
  width: '100%',
  minHeight: '100%',
  display: 'flex',
  alignItems: 'center',
};

const BOLD_START = '[[b]]';
const BOLD_END = '[[/b]]';

const renderTooltipTitle = (tooltipMessages?: string[] | null): React.ReactNode => {
  const text = tooltipMessages?.join(' ') ?? '';

  // Fast path: keep existing behavior if no formatting markers are present.
  if (!text.includes(BOLD_START) || !text.includes(BOLD_END)) {
    return text;
  }

  const regex = /\[\[b\]\]([\s\S]*?)\[\[\/b\]\]/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null = regex.exec(text);
  let i = 0;

  while (match !== null) {
    const start = match.index;
    const end = regex.lastIndex;

    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    parts.push(
      <strong key={`bold-${i}`} style={{ fontWeight: 700 }}>
        {match[1]}
      </strong>
    );

    lastIndex = end;
    i += 1;
    match = regex.exec(text);
  }

  if (!parts.length) {
    return text;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <span>{parts}</span>;
};

// Memoized function to determine whether the tooltip should be displayed
const shouldShowTooltip = (key?: string, tooltipMessages?: string[] | null, dialogs?: any[], alias?: string) =>
  !isEmpty(tooltipMessages) && !dialogs?.some(({ cellId, alias: cellAlias }) => cellId === key && cellAlias === alias);

const withTooltip = (WrappedComponent: ComponentType<any>) => {
  const WithTooltip = (props: WithTooltipProps) => {
    const { cell } = props;
    const { alias, key, tooltipMessages = [] } = cell;

    // Styles for the tooltip label

    const { isExportMode } = useLayoutContext();
    const { dialogs = [] } = useDataSheetDialogActions();

    const shouldRenderTooltip = shouldShowTooltip(key, tooltipMessages, dialogs, alias);

    const title = useMemo(() => (shouldRenderTooltip ? renderTooltipTitle(tooltipMessages) : null), [
      shouldRenderTooltip,
      tooltipMessages,
    ]);

    // Memoize the tooltip placement to prevent unnecessary recalculations
    const tooltipPlacement = useMemo(() => cell?.tooltipPlacement ?? 'top', [cell?.tooltipPlacement]);

    const popperOptions = useMemo(() => (cell.positionFixed ? { positionFixed: true } : {}), [cell.positionFixed]);

    const disableHoverEvent = useMemo(
      () => dialogs?.some(({ cellId = null, alias: cellAlias = null }) => cellId === key && cellAlias === alias),
      [alias, dialogs, key]
    );

    if (shouldRenderTooltip) {
      if (isExportMode) {
        return <WrappedComponent {...props} />;
      }

      return (
        <Tooltip
          arrow
          classes={{ tooltip: classes.tooltip }}
          disableHoverListener={disableHoverEvent}
          disableTouchListener={disableHoverEvent}
          placement={tooltipPlacement}
          PopperProps={{
            disablePortal: !!cell.insideLedger,
            popperOptions,
          }}
          title={title ?? ''}>
          <div style={styles}>
            <div className="tooltip-indicator" />
            <WrappedComponent {...props} />
          </div>
        </Tooltip>
      );
    }

    return <WrappedComponent {...props} />;
  };

  return WithTooltip;
};

export default withTooltip;
